/**
 * Every attendance change needs a reason (spec §4.2): a drop-down of common reasons plus free text.
 */
import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { REASONS } from './shared';

export default function ReasonDialog({ open, title, description, confirmLabel, isRTL, optional = false,
  destructive = false, children, onCancel, onConfirm }) {
  const [preset, setPreset] = useState('');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const t = (en, ar) => (isRTL ? ar : en);

  useEffect(() => { if (open) { setPreset(''); setText(''); setBusy(false); } }, [open]);

  const reason = [preset, text.trim()].filter(Boolean).join(' — ');
  const ok = optional || reason.length >= 3;

  const confirm = async () => {
    setBusy(true);
    try { await onConfirm(reason); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onCancel(); }}>
      <DialogContent dir={isRTL ? 'rtl' : 'ltr'} data-testid="attendance-reason-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        {children}
        <div className="space-y-2">
          <select
            className="w-full border rounded-md px-3 py-2 text-sm bg-white"
            value={preset}
            onChange={(e) => setPreset(e.target.value)}
            data-testid="attendance-reason-preset"
          >
            <option value="">{t('Choose a reason…', 'اختر سبباً…')}</option>
            {REASONS.map((r) => (
              <option key={r.en} value={isRTL ? r.ar : r.en}>{isRTL ? r.ar : r.en}</option>
            ))}
          </select>
          <Textarea
            rows={3}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={t('Details (optional if a reason is chosen)', 'تفاصيل (اختيارية إذا اخترت سبباً)')}
            data-testid="attendance-reason-text"
          />
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onCancel}>{t('Cancel', 'إلغاء')}</Button>
          <Button variant={destructive ? 'destructive' : 'default'} onClick={confirm} disabled={!ok || busy}
            data-testid="attendance-reason-confirm">
            {busy && <Loader2 className="h-4 w-4 animate-spin me-1" />}
            {confirmLabel || t('Confirm', 'تأكيد')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
